import { RootState } from "../index";
import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import {
  groupApproveSticker,
  createSticker,
  destroySticker,
  fetchSticker,
  fetchStickers,
  updateSticker, groupDestroySticker
} from "../../actions/sticker_maker/stickersActions";

export interface StickerInterface {
  id: number;
  sticker_pack_id: number;
  sticker_pack?: number;

  likes_count: number;
  popular_count: number;

  status: number;

  image_url: string;
  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  newParams: StickerInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  newParams: undefined,
  newError: null
};

const stickersAdapter = createEntityAdapter<StickerInterface>();

const slice = createSlice({
  name: 'stickers',
  initialState: stickersAdapter.getInitialState(initialState),
  reducers: {
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchStickers.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchStickers.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.stickers;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchStickers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })

      // #show
      .addCase(fetchSticker.fulfilled, (state, action) => {
        const sticker = Object.values(action.payload.sticker)[0];
        state.entities[sticker.id] = sticker;
      })

      // #create
      .addCase(createSticker.fulfilled, (state, action) => {
        const sticker = Object.values(action.payload.sticker)[0];
        state.entities[sticker.id] = sticker;
        state.ids.unshift(sticker.id);
      })
      .addCase(createSticker.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })

      // #update
      .addCase(updateSticker.fulfilled, (state, action) => {
        const sticker = Object.values(action.payload.sticker)[0];
        state.entities[sticker.id] = sticker;
      })
      .addCase(updateSticker.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })
      .addCase(groupApproveSticker.fulfilled, (state, action) => {
        state.entities = {...state.entities, ...action.payload.stickers};
      })

      // #destroy
      .addCase(destroySticker.fulfilled, (state, action) => {
        stickersAdapter.removeOne(state, action.payload.id);
      })
      .addCase(groupDestroySticker.fulfilled, (state, action) => {
        stickersAdapter.removeMany(state, action.payload.ids);
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setEditError
} = slice.actions;

export const {
  selectById: selectStickerById,
  selectIds: selectStickerIds,
  selectEntities: selectStickerEntities,
  selectAll: selectAllStickers,
} = stickersAdapter.getSelectors<RootState>(state => state.stickerMaker.stickers);
